import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigation, useRouter } from 'expo-router';

import { HapticNotificationFeedbackStyle, triggerHapticNotificationFeedback } from '@sg/garnish';
import { IngredientAssetVersion } from '@sg/graphql-schema';

import {
  Bundle,
  type BundleContentHandle,
  type BundleMachineTypes,
  type BundleSelectProduct,
  type BundleSelectedProduct,
  useBundleMachine,
  useInitBundleMachine,
} from '@order/components-menu';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { NotFoundView } from '../../components';
import { useDeliveryOrderInFlight } from '../../hooks';
import { handleHardwareBackButtonPress, useNavigateBack } from '../../navigation';
import {
  useBundleAddedToBagNotice,
  useBundleAddToBagActors,
  useBundleFailedToAddToBagNotice,
  useBundleRemoteData,
  useBundleTelemetry,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleScreen = () => {
  const isBundleEnabled = useFeatureFlag('CELS-5880-order-product-bundles-enabled');
  const navigateBack = useNavigateBack();

  if (!isBundleEnabled) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <NotFoundView withoutFooter />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  return <BundleScreenLoader />;
};

// ─────────────────────────────────────────────────────────────────────────────

const BundleScreenLoader = () => {
  const router = useRouter();
  const navigateBack = useNavigateBack();

  const { content, products, productsBySlug, restaurantSlug, isDelivery, hasLocation, isLoading } =
    useBundleRemoteData();

  // ─── Handlers ────────────────────────────────────────────────────────

  // NOTE: Pushed rather than redirected, so this screen stays underneath and
  // the location search can present as a sheet over it.
  const navigateToLocationSearch = useCallback(() => {
    router.push('/bundle/search-location');
  }, [router]);

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <Bundle.Loading />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  if (!hasLocation) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <Bundle.NoLocation
          title={content.selectLocationTitle}
          description={content.selectLocationDescription}
          ctaLabel={content.selectLocationCta}
          onSelectLocation={navigateToLocationSearch}
        />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  if (!restaurantSlug) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <NotFoundView withoutFooter />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  return (
    <BundleScreenReady
      // NOTE: Keyed by location, so a location change starts a fresh bundle machine.
      key={`${restaurantSlug}:${isDelivery}`}
      content={content}
      products={products}
      productsBySlug={productsBySlug}
      restaurantSlug={restaurantSlug}
      isDelivery={isDelivery}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const BundleScreenReady = (props: Props) => {
  const { content, products, productsBySlug, restaurantSlug, isDelivery } = props;

  const router = useRouter();
  const navigation = useNavigation();
  const navigateBack = useNavigateBack();
  const isUpsellPricingEnabled = useFeatureFlag('CELS-5026-customization-upsell-pricing-enabled');

  const { customization, captureDeliveryDetails, attributeAddedLineItems } =
    useBundleAddToBagActors();
  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  const {
    trackView,
    trackSelectModalView,
    trackProductSelected,
    trackProductRemoved,
    trackProductUnavailableTapped,
    trackAbandoned,
    trackAddToBagPressed,
    trackAddedToBag,
    trackFailedToAddToBag,
  } = useBundleTelemetry();

  // ─── Refs ────────────────────────────────────────────────────────────

  const contentRef = useRef<BundleContentHandle>(null);

  // ─── Machine ─────────────────────────────────────────────────────────

  const showAddedToBagNotice = useBundleAddedToBagNotice({
    productsBySlug,
    successBanner: content.successBanner,
    partialSuccessBanner: content.partialSuccessBanner,
  });

  const showFailedToAddToBagNotice = useBundleFailedToAddToBagNotice({
    failureBanner: content.failureBanner,
  });

  const handleProductConfirmed = useCallback(() => {
    contentRef.current?.scrollToEnd();
  }, []);

  const handleAddedToBag = useCallback(
    (_actionArgs: unknown, summary: AddToBagSummary) => {
      attributeAddedLineItems(summary.addedProductIds);
      trackAddedToBag(summary);

      void triggerHapticNotificationFeedback(HapticNotificationFeedbackStyle.Success);
      showAddedToBagNotice(summary);
      router.dismissTo('/bag');
    },
    [attributeAddedLineItems, router, showAddedToBagNotice, trackAddedToBag],
  );

  const handleFailedToAddToBag = useCallback(
    (_actionArgs: unknown, summary: AddToBagSummary) => {
      trackFailedToAddToBag(summary);
      showFailedToAddToBagNotice();
    },
    [showFailedToAddToBagNotice, trackFailedToAddToBag],
  );

  const bundleActorRef = useInitBundleMachine(
    {
      restaurantSlug,
      isDelivery,
      ingredientAssetVersion: IngredientAssetVersion.V2,
      isUpsellPricingEnabled,
    },
    {
      actors: { customization },
      actions: {
        onProductConfirmed: handleProductConfirmed,
        onAddedToBag: handleAddedToBag,
        onFailedToAddToBag: handleFailedToAddToBag,
      },
    },
  );

  const {
    selectedProducts,
    selectedCount,
    canSelectMore,
    pendingProduct,
    isSelecting,
    isLocked,
    addToBagStatus,
    settledProductCount,
    openSelect,
    selectProduct,
    confirmSelect,
    closeSelect,
    removeSelected,
    addToBag,
  } = useBundleMachine(bundleActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const selectedItems = useMemo(() => {
    return selectedProducts
      .map(({ productSlug, selectionId, outOfStockIngredientNames, netPrice = 0 }) => {
        const product = productsBySlug.get(productSlug);

        if (!product) return;

        return {
          ...product,
          cost: product.cost + netPrice,
          selectionId,
          outOfStockIngredientNames,
        };
      })
      .filter((product) => product !== undefined);
  }, [productsBySlug, selectedProducts]);

  const totalCost = selectedItems.reduce(addSelectedItemCost, 0);
  const hasDeliveryOrderInFlight = isDelivery && Boolean(inFlightDeliveryOrderId);

  const outOfStockProductSlugs = selectedProducts
    .filter((selectedProduct) => Boolean(selectedProduct.outOfStockIngredientNames))
    .map(getSelectedProductSlug);

  const outOfStockIngredientNames = selectedProducts
    .map((selectedProduct) => selectedProduct.outOfStockIngredientNames)
    .filter((ingredientNames) => ingredientNames !== undefined);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    if (isLocked) return;

    if (selectedCount > 0) {
      trackAbandoned({ selectedCount, totalCost });
    }

    navigateBack();
  }, [isLocked, navigateBack, selectedCount, totalCost, trackAbandoned]);

  const handleOpenSelect = useCallback(() => {
    trackSelectModalView(selectedCount);
    openSelect();
  }, [openSelect, selectedCount, trackSelectModalView]);

  const handleConfirmSelect = useCallback(() => {
    const selectedProduct = pendingProduct
      ? productsBySlug.get(pendingProduct.productSlug)
      : undefined;

    if (selectedProduct) {
      trackProductSelected({
        productId: selectedProduct.id,
        productSlug: selectedProduct.slug,
        productName: selectedProduct.name,
        cost: selectedProduct.cost,
        selectedCount: selectedCount + 1,
      });
    }

    confirmSelect();
  }, [confirmSelect, pendingProduct, productsBySlug, selectedCount, trackProductSelected]);

  const handleRemoveSelected = useCallback(
    (selectionId: string) => {
      const removedItem = selectedItems.find(
        (selectedItem) => selectedItem.selectionId === selectionId,
      );

      if (removedItem) {
        trackProductRemoved({ productSlug: removedItem.slug, remainingCount: selectedCount - 1 });
      }

      removeSelected(selectionId);
    },
    [removeSelected, selectedCount, selectedItems, trackProductRemoved],
  );

  const handleUnavailableSelect = useCallback(
    (product: BundleSelectProduct) => {
      trackProductUnavailableTapped({ productId: product.id, productSlug: product.slug });
    },
    [trackProductUnavailableTapped],
  );

  const handleAddToBag = useCallback(() => {
    trackAddToBagPressed({
      selectedCount,
      totalCost,
      outOfStockProductSlugs,
      outOfStockIngredientNames,
    });

    if (hasDeliveryOrderInFlight) {
      navigateToDeliveryOrderInFlightScreen();

      return;
    }

    captureDeliveryDetails();
    addToBag();
  }, [
    addToBag,
    captureDeliveryDetails,
    hasDeliveryOrderInFlight,
    navigateToDeliveryOrderInFlightScreen,
    outOfStockIngredientNames,
    outOfStockProductSlugs,
    selectedCount,
    totalCost,
    trackAddToBagPressed,
  ]);

  const handleSelect = useCallback(
    (product: BundleSelectProduct) => {
      selectProduct({ productSlug: product.slug, productId: product.id });
    },
    [selectProduct],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(trackView, [trackView]);

  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !isLocked });
  }, [isLocked, navigation]);

  useEffect(() => {
    return handleHardwareBackButtonPress(() => isLocked);
  }, [isLocked]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Bundle.Container onDismiss={handleDismiss}>
        <Bundle.Content ref={contentRef}>
          <Bundle.Hero imageUrl={content.imageUrl} imageA11y={content.imageA11y} />

          <Bundle.Copy name={content.name} description={content.description} />

          <Bundle.Included selectedCount={selectedCount}>
            {selectedItems.map((selectedItem) => (
              <Bundle.SelectedItem
                key={selectedItem.selectionId}
                product={selectedItem}
                disabled={isLocked}
                onRemove={handleRemoveSelected}
              />
            ))}

            {canSelectMore ? (
              <Bundle.Slot
                label={content.selectTitle}
                disabled={isLocked}
                onPress={handleOpenSelect}
              />
            ) : null}
          </Bundle.Included>
        </Bundle.Content>

        <Bundle.Footer>
          <Bundle.AddToBagButton
            status={addToBagStatus}
            settledCount={settledProductCount}
            totalCount={selectedCount}
            totalCost={totalCost}
            disabled={selectedCount === 0}
            onPress={handleAddToBag}
          />
        </Bundle.Footer>

        <Bundle.CloseButton disabled={isLocked} onPress={handleDismiss} />
      </Bundle.Container>

      <Bundle.SelectModal
        isVisible={isSelecting}
        title={content.selectTitle}
        products={products}
        selectedProductSlug={pendingProduct?.productSlug}
        onSelect={handleSelect}
        onUnavailableSelect={handleUnavailableSelect}
        onClose={closeSelect}
        onContinue={handleConfirmSelect}
      />
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function addSelectedItemCost(totalCost: number, selectedItem: BundleSelectedProduct) {
  return totalCost + selectedItem.cost;
}

function getSelectedProductSlug(selectedProduct: SelectedProduct) {
  return selectedProduct.productSlug;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Pick<RemoteData, 'content' | 'products' | 'productsBySlug' | 'isDelivery'> & {
  restaurantSlug: string;
};

type RemoteData = ReturnType<typeof useBundleRemoteData>;

type AddToBagSummary = BundleMachineTypes.AddToBagSummary;

type SelectedProduct = BundleMachineTypes.SelectedProduct;
